
import {
    Result,
    failure,
    success,
} from '@superbet-group/web.lib.utilities';

import { VideoProviderType } from '@core/models/offer/enums';
import {
    BetGeniusError,
    BetGeniusRequest,
    BetGeniusStream,
    isBetGeniusError,
} from '@models/streams/BetGenius';
import {
    BetRadarError,
    isBetRadarError,
} from '@models/streams/BetRadar';
import {
    ImgArenaError,
    ImgArenaRequest,
    isImgArenaError,
} from '@models/streams/ImgArena';
import {
    InfrontsportsError,
    isInfrontsportsError,
} from '@models/streams/Infrontsports';
import {
    PerformError,
    isPerformTokenError,
    Perform,
} from '@models/streams/Perform';

import RestService from './_private/RestService';
import { VideoType } from './enums';
import type {
    AtLeastOneVideoProvider,
    StreamData,
} from './types';

export default class MediaService {
    constructor(private restService = new RestService()) {}

    public getMatchVideoStream(providers: AtLeastOneVideoProvider, userId: number) {
        if (providers[VideoProviderType.IMG]) {
            return this.getImgArena(providers[VideoProviderType.IMG]!);
        } else if (providers[VideoProviderType.INFRONTSPORTS]) {
            return this.getInfrontsports(providers[VideoProviderType.INFRONTSPORTS]!, userId);
        } else {
            return this.getBetradar(providers[VideoProviderType.BETRADAR]!);
        }
    }

    async getImgArena(matchId: number): Promise<Result<StreamData, ImgArenaError>> {
        const tokenResponse = await this.restService.getImgArenaToken();

        if (isImgArenaError(tokenResponse)) {
            return failure(tokenResponse);
        }

        const imgStreamParams = {
            url: tokenResponse?.url,
            clientId: tokenResponse?.client_id,
            timems: tokenResponse?.timestamp,
            md5: tokenResponse?.access_token,
        } as ImgArenaRequest;

        const response = await this.restService.getImgArenaStreamData(matchId!, imgStreamParams);

        if (isImgArenaError(response)) {
            return failure(response);
        }

        return success({
            streamUrl: response?.hlsUrl || '',
            videoType: VideoType.hls,
        });
    }

    async getBetradar(matchId: number): Promise<Result<StreamData, BetRadarError>> {
        const response = await this.restService.getBetRadarStreamData(matchId);

        if (isBetRadarError(response)) {
            return failure(response);
        }

        return success({
            streamUrl: response?.stream_url || '',
            videoType: VideoType.m3u8,
        });
    }

    async getInfrontsports(matchId: string, userId: number): Promise<Result<StreamData, InfrontsportsError>> {
        const response = await this.restService.getInfrontsportsStreamData(matchId, userId);

        if (isInfrontsportsError(response)) {
            return failure(response);
        }

        return success({
            streamUrl: response?.stream_url || '',
            videoType: VideoType.hls,
        });
    }

    public async getPerform(streamId: string, uuid: string): Promise<Result<Perform, PerformError>> {
        const tokenResponse = await this.restService.getPerformToken(uuid, streamId);

        if (isPerformTokenError(tokenResponse)) {
            return failure(tokenResponse);
        }

        return success({
            hashedUsername: tokenResponse?.uuid || '',
            token: tokenResponse?.access_token,
        });
    }

    public async getBetGenius(request: BetGeniusRequest): Promise<Result<BetGeniusStream, BetGeniusError>> {
        const response = await this.restService.getBetGenius(request);

        if (isBetGeniusError(response)) {
            return failure(response);
        }

        return success(response);
    }
}
